import { Component, effect, inject, OnInit, Signal, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { VehiclesApiServiceObservable } from '@kempoweroyj/ngx-vehicles-api-client';
import { AnalyticsService, CookieService, PageViewTrackingService } from '@kempower-nx-workspace/chargeye-shared';
import {
    NotificationMessage,
    NotificationsManagementService,
    UserNotification,
} from '@kempower-nx-workspace/chargeye-notifications-management';
import {
    CeDialogService,
    CeSnackbarService,
    CeSnackbarState,
    CeToastNotification,
    CeToastNotificationComponent,
    CeToastNotificationService,
    ModalSizeOption,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { CeCookieBannerComponent, NetworkStatusService } from '@kempower-nx-workspace/chargeye-core';
import { AuthenticationService as Auth0AuthenticationService } from '@kempower-nx-workspace/auth0-authentications';
import { toSignal } from '@angular/core/rxjs-interop';
import { ExternalNotificationsApiServiceObservable, PutIndicatorResponse } from '@kempoweroyj/ngx-external-notifications-api-client';
import { UnreadNotificationIndicatorService } from '@kempower-nx-workspace/notifications';
import { AuthenticationService } from '@kempower-nx-workspace/ce-authentication';
import { environment, EnvService } from '@kempower-nx-workspace/ce-environment';
import { SidePanelNavigationService } from '@kempower-nx-workspace/ce-side-navigation';
import { InternalTokenService } from '@kempower-nx-workspace/chargeye-content';
import { ProductUpdateBadgeService } from '@kempower-nx-workspace/product-update';

@Component({
    imports: [RouterModule, CeToastNotificationComponent],
    selector: 'kempower-nx-workspace-root',
    providers: [
        SidePanelNavigationService,
        VehiclesApiServiceObservable,
        CookieService,
        ExternalNotificationsApiServiceObservable,
        UnreadNotificationIndicatorService,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
    readonly #router = inject(Router);
    readonly #cookieService = inject(CookieService);
    readonly #dialogService = inject(CeDialogService);
    readonly #analyticsService = inject(AnalyticsService);
    readonly #pageViewTracking = inject(PageViewTrackingService);
    readonly #networkService = inject(NetworkStatusService);
    readonly #tokenExpirationService = inject(Auth0AuthenticationService);
    readonly #snackbarService = inject(CeSnackbarService);
    readonly #toastNotificationService = inject(CeToastNotificationService);
    readonly #notificationsManagement = inject(NotificationsManagementService);
    readonly #sidePanelNavigationService = inject(SidePanelNavigationService);
    readonly #unreadNotificationIndicatorService = inject(UnreadNotificationIndicatorService);
    readonly #internalTokenService = inject(InternalTokenService);
    readonly #productUpdateBadgeService = inject(ProductUpdateBadgeService);
    readonly auth = inject(AuthenticationService); // need to enhance auth checking

    readonly websocketMessage: Signal<NotificationMessage | undefined> = toSignal(
        this.#notificationsManagement.notifications$().pipe()
    );
    readonly newNotificationIndicator: Signal<boolean | undefined> = toSignal(
        this.#unreadNotificationIndicatorService.getUnreadIndicator$()
    );
    organizationId = signal('');
    websocketMessageTimeStamp = signal('');

    /**
     * Fetch product update badge count once on auth (internal token first for sysadmin).
     */
    fetchProductUpdateBadge = effect(() => {
        if (this.auth.isAuthenticated()) {
            const isSysAdmin = this.auth.getCsmsAuthProfilePermissions().isSystemAdmin ?? false;
            this.#internalTokenService.fetchToken(isSysAdmin).then(() => {
                this.#productUpdateBadgeService.fetchUnreadCount(
                    environment.contentCdn?.stage ?? environment.appConfig.envType
                );
            });
        }
    });

    /**
     * Sync product update unread count to side nav reactively.
     */
    syncProductUpdateBadge = effect(
        () => {
            this.#sidePanelNavigationService.newProductUpdateCount.set(
                this.#productUpdateBadgeService.unreadCount()
            );
        },
        { allowSignalWrites: true }
    );

    /**
     * Connect to Websocket to show toast notifications
     */
    connectToWebsocket = effect(() => {
        if (this.auth.isAuthenticated()) {
            // Once logged in, connect/disconnect depending on network's status
            if (this.#networkService.isOnline()) {
                this.#notificationsManagement.connect();
            } else {
                this.#notificationsManagement.disconnect();
            }
        }
    });

    /**
     * Get organization ID
     */
    updateOrganizationId = effect(
        () => {
            this.organizationId.set(this.auth.profileStore()?.tenantId as string);
        },
        { allowSignalWrites: true }
    );

    /**
     * Set organization ID as toast identifier, so correct toasts can be shown
     */
    setOrganizationIdAsToastIdentifier = effect(
        () => {
            if (this.auth.isAuthenticated() && this.auth.profileStore() && this.organizationId()) {
                this.#toastNotificationService.toastId.set(this.organizationId());
            }
        },
        { allowSignalWrites: true }
    );

    /**
     * Show toast notification when new notification arrives
     */
    showToastNotification = effect(
        () => {
            if (
                this.auth.isAuthenticated() &&
                this.websocketMessage()?.browserReceiveTimestamp !== this.websocketMessageTimeStamp() &&
                this.websocketMessage()?.type === 'UserNotification'
            ) {
                this.websocketMessageTimeStamp.set(this.websocketMessage()?.browserReceiveTimestamp as string);
                const newNotification: CeToastNotification = {
                    message: (this.websocketMessage() as UserNotification)?.title as string,
                    actionHref: `/notifications/inbox/${(this.websocketMessage() as UserNotification)?.notificationId}`,
                };
                this.#toastNotificationService.addNotification(newNotification);

                if (!this.#router.url.includes('notifications/inbox')) {
                    // Add "new notifications indicator" to SidePanel if the current page is some else than notifications' "Inbox"
                    this.#sidePanelNavigationService.hasNewNotification.set(true);
                } else {
                    // Set "new notifications indicator" false, when user is in notifications' "Inbox".
                    // Otherwise, the indicator will falsely appear when navigating somewhere else.
                    this.#unreadNotificationIndicatorService
                        .setUnreadIndicatorFalse$()
                        .subscribe((res: PutIndicatorResponse) => {
                            if (res !== undefined) {
                                this.#sidePanelNavigationService.hasNewNotification.set(false);
                            }
                        });
                }
            }
        },
        { allowSignalWrites: true }
    );

    // Show "new notifications indicator" in SidePanel if user has new notifications (since the last visit or during the same session)
    updateSidePanelUnreadIndicator = effect(
        () => {
            if (this.auth.isAuthenticated() && this.newNotificationIndicator()) {
                this.#sidePanelNavigationService.hasNewNotification.set(this.newNotificationIndicator() as boolean);
            }
        },
        { allowSignalWrites: true }
    );

    /**
     * Show session expired snackbar and redirect to login when the token expires.
     */
    sessionExpiredEffect = effect(() => {
        if (this.#tokenExpirationService.isSessionExpired() && !this.#router.url.startsWith('/auth')) {
            const redirectUrl = encodeURIComponent(this.#router.url);
            this.#snackbarService.closeSnackbar();
            this.#snackbarService.showSnackbar({
                message: 'Your session has expired \u2014 log in to continue.',
                duration: 'persist',
                state: CeSnackbarState.ERROR,
                icon: 'warning-hexagon',
                actions: [
                    {
                        label: 'Log in',
                        onClick: () => {
                            this.#router.navigate(['/auth/login'], { queryParams: { redirectUrl } });
                        },
                    },
                ],
            });
        }
    });

    /**
     * Initialise core services required to start the application.
     *
     * These methods are responsible for activating essential features such as
     * cookie consent checks, analytics initialization, and other startup
     * logic necessary for the application to run properly.
     * It ensures that critical services are configured and operational
     * before the rest of the app lifecycle continues.
     * Keep this component lean and offload to services.
     */

    /**
     * Shows a welcome snackbar immediately when the user completes a fresh login.
     * Fires as soon as isLoginEvent is set by the guard — no dependency on profileStore.
     */
    showLoginSnackbar = effect(() => {
        if (this.auth.isLoginEvent()) {
            this.#snackbarService.showSnackbar({
                message: 'Logged in',
                duration: 4000,
                icon: 'checkmark',
            });
        }
    });

    initialiseWhenAuthenticated = effect(
        () => {
            const userProfile = this.auth.profileStore();
            if (this.auth.isAuthenticated() && userProfile) {
                this.#analyticsService.initTracking(userProfile);
            }
            if (this.auth.isLoginEvent() && userProfile?.organizationName) {
                if (this.#tokenExpirationService.checkTokenRestored()) {
                    this.#snackbarService.closeSnackbar();
                    this.#snackbarService.showSnackbar({
                        message: 'Session restored',
                        duration: 5000,
                        icon: 'checkmark',
                    });
                }
                this.#analyticsService.sendLoginEvent();
                this.auth.isLoginEvent.set(false); // Reset login event after processing
            }
        },
        { allowSignalWrites: true }
    );

    ngOnInit(): void {
        if (!EnvService.isLocalHost() && this.#cookieService.readConsentChoice() === undefined) {
            this.#dialogService.openDynamicDialog(CeCookieBannerComponent, {
                size: ModalSizeOption.MEDIUM,
                preventCloseOnEsc: true,
                closeOnBackdropClick: false,
            });
        }
        this.#networkService.initOfflineOnline();
        this.#tokenExpirationService.initTokenExpirationWatch();

        // Take over GA4 page_view firing so each hit carries the final, settled page title.
        this.#pageViewTracking.init();
    }
}
